// src/services/trackerRepo.ts
import { TrackerEntry, BankrollState, BankrollTx } from '../domain/types';

// ── Interface ──
export interface TrackerRepo {
  // entries
  getAll(): Promise<TrackerEntry[]>;
  upsert(entry: TrackerEntry): Promise<void>;
  remove(id: string): Promise<void>;
  clear(): Promise<void>;

  // bankroll
  getBankroll(): Promise<BankrollState>;
  setBankroll(state: BankrollState): Promise<void>;
  addTx(tx: BankrollTx): Promise<void>;
}

// ── Implementação IndexedDB (idb) ──
import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface RushDB extends DBSchema {
  entries: { key: string; value: TrackerEntry; indexes: { 'by-type': string; 'by-status': string } };
  bankroll: { key: string; value: BankrollState };
  txs: { key: string; value: BankrollTx; indexes: { 'by-ref': string } };
}

let dbPromise: Promise<IDBPDatabase<RushDB>> | null = null;

const getDB = () => {
  if (!dbPromise) {
    dbPromise = openDB<RushDB>('rush-props', 1, {
      upgrade(db) {
        const e = db.createObjectStore('entries', { keyPath: 'id' });
        e.createIndex('by-type', 'type');
        e.createIndex('by-status', 'data.status');
        db.createObjectStore('bankroll', { keyPath: 'id' });
        const t = db.createObjectStore('txs', { keyPath: 'id' });
        t.createIndex('by-ref', 'refId');
      },
    });
  }
  return dbPromise;
};

export const indexedDBRepo: TrackerRepo = {
  async getAll() {
    const db = await getDB();
    return db.getAll('entries');
  },
  async upsert(entry) {
    const db = await getDB();
    await db.put('entries', entry);
  },
  async remove(id) {
    const db = await getDB();
    await db.delete('entries', id);
  },
  async clear() {
    const db = await getDB();
    await db.clear('entries');
  },

  async getBankroll() {
    const db = await getDB();
    return (await db.get('bankroll', 'main')) ?? initialBankrollState();
  },
  async setBankroll(state) {
    const db = await getDB();
    await db.put('bankroll', { id: 'main', ...state });
  },
  async addTx(tx) {
    const db = await getDB();
    await db.put('txs', tx);
  },
};

function initialBankrollState(): BankrollState {
  return { initial: 100, current: 100, openStake: 0, history: [] };
}
